// =============================================================================
// SANKEY DATA BUILDER UTILITIES
// =============================================================================

import type { SankeyData, SankeyNodeData } from '@components/shared/sankey.component';
import { select } from 'd3-selection';

// =============================================================================
// COLOR SCHEMES
// =============================================================================

/**
 * Available color schemes for Sankey diagrams
 */
export type SankeyColorScheme = 'default' | 'category' | 'technology' | 'process';

/**
 * Predefined color schemes for Sankey diagrams
 * Moved from backend to centralize color management in frontend
 */
export const SANKEY_COLOR_SCHEMES: Record<SankeyColorScheme, Record<string, string>> = {
  default: {
    // D3 Category10 colors
    type1: '#1f77b4',
    type2: '#ff7f0e',
    type3: '#2ca02c',
    type4: '#d62728',
    type5: '#9467bd',
    type6: '#8c564b',
    type7: '#e377c2',
    type8: '#7f7f7f',
    type9: '#bcbd22',
    type10: '#17becf',
  },
  category: {
    // Technology categories
    software: '#3B82F6', // Blue
    hardware: '#10B981', // Emerald
    hybrid: '#F59E0B', // Amber
    system: '#8B5CF6', // Violet
    component: '#EC4899', // Pink
    platform: '#06B6D4', // Cyan
  },
  technology: {
    // Technology nodes
    technology: '#818CF8', // Indigo
    subsystem: '#F43F5E', // Rose
    component: '#EC4899', // Pink
    origin: '#F59E0B', // Amber
    domain: '#10B981', // Emerald
    use: '#06B6D4', // Cyan
  },
  process: {
    // Sequential process
    start: '#10B981', // Green
    early: '#84CC16', // Lime
    middle: '#F59E0B', // Amber
    late: '#F97316', // Orange
    end: '#EF4444', // Red
    trl_1_3: '#10B981', // Green (Early TRL)
    trl_4_6: '#F59E0B', // Amber (Mid TRL)
    trl_7_9: '#3B82F6', // Blue (High TRL)
  },
};

/**
 * Default color pool for dynamic Sankey diagrams
 * Used when types don't match any predefined color scheme
 */
export const DEFAULT_COLOR_POOL: string[] = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#EF4444', // Red
  '#84CC16', // Lime
  '#818CF8', // Indigo
  '#F43F5E', // Rose
  '#14B8A6', // Teal
];

/**
 * Assigns a color from a pool based on index
 * @param index - The index to use for color selection
 * @param pool - The color pool to select from (defaults to DEFAULT_COLOR_POOL)
 * @returns The color string
 */
export function assignColorFromPool(index: number, pool: string[] = DEFAULT_COLOR_POOL): string {
  return pool[index % pool.length];
}

/**
 * Gets the color for a specific type from a color scheme
 * @param type - The node type to get color for
 * @param scheme - The color scheme to use
 * @returns The color string if found, undefined otherwise
 */
export function getColorForType(type: string, scheme: SankeyColorScheme): string | undefined {
  const schemeColors = SANKEY_COLOR_SCHEMES[scheme];
  return schemeColors[type.toLowerCase()];
}

/**
 * Gets all colors from a color scheme as a Record
 * @param scheme - The color scheme to get colors from
 * @returns Record mapping types to colors
 */
export function getColorMapFromScheme(scheme: SankeyColorScheme): Record<string, string> {
  return { ...SANKEY_COLOR_SCHEMES[scheme] };
}

// =============================================================================
// SANKEY DATA BUILDER
// =============================================================================

/**
 * Parameters for adding a node to the Sankey data structure
 */
export type AddNodeParams<N = Record<string, unknown>> = {
  /** Additional data to include with the node */
  extraData?: Partial<N>;
  /** The node name */
  name: string;
  /** The node type for the category */
  type: string;
};

/**
 * Parameters for adding a link between two nodes
 */
export type AddLinkParams = {
  /** The source node name */
  sourceName: string;
  /** The source node type */
  sourceType: string;
  /** The target node name */
  targetName: string;
  /** The target node type */
  targetType: string;
  /** The link value */
  value: number;
};

/**
 * A builder class for constructing Sankey data with unique node IDs and link aggregation
 *
 * CRITICAL: This class uses the "type:name" pattern for node IDs to support reusing
 * the same name across different types/columns. For example:
 * - "domain:Infrastructure" and "use:Infrastructure" are two distinct nodes
 *
 * This pattern is essential for:
 * 1. Allowing duplicate names in different columns
 * 2. Proper node lookup in the Sankey component
 * 3. Correct link resolution between nodes
 */
export class SankeyDataBuilder<N = Record<string, unknown>> {
  private readonly data: SankeyData<Record<string, unknown>, N> = {
    links: [],
    nodes: [],
  };
  private readonly nodeIdMap = new Map<string, string>();

  /**
   * Adds a link between two nodes, aggregating values if the link already exists
   * NOTE: Both source and target nodes must exist before creating links
   * @param params - Link parameters including source, target, and value
   */
  addLink(params: AddLinkParams): void {
    const { sourceName, sourceType, targetName, targetType, value } = params;
    const sourceId = this.createNodeId(sourceType, sourceName);
    const targetId = this.createNodeId(targetType, targetName);

    // Prevent circular links
    if (sourceId === targetId) {
      console.warn(`Attempted to create circular link: ${sourceId} -> ${targetId}`);
      return;
    }

    // Validate that both nodes exist
    if (!this.nodeIdMap.has(sourceId) || !this.nodeIdMap.has(targetId)) {
      console.warn(`Invalid link: ${sourceId} -> ${targetId}. One or both nodes don't exist.`);
      return;
    }

    // Check if link already exists and aggregate
    const existingLink = this.data.links.find((link) => link.source === sourceId && link.target === targetId);

    if (existingLink) {
      existingLink.value += value;
    } else {
      this.data.links.push({ source: sourceId, target: targetId, value });
    }
  }

  /**
   * Adds a unique node to the data structure
   * NOTE: Nodes must be added before creating links that reference them
   * @param params - Node parameters including name, type, and optional extra data
   * @returns The unique node ID that was created or already existed
   */
  addNode(params: AddNodeParams<N>): string {
    const { name, type, extraData = {} } = params;
    const nodeId = this.createNodeId(type, name);

    // Check if this exact node already exists
    if (this.nodeIdMap.has(nodeId)) {
      return nodeId;
    }

    this.nodeIdMap.set(nodeId, nodeId);

    this.data.nodes.push({
      category: type, // Use type for both category (color mapping) and type (business logic)
      id: nodeId,
      name,
      ...extraData,
    } as SankeyNodeData<N>);

    return nodeId;
  }

  /**
   * Creates a unique node ID using the pattern: type:name
   *
   * CRITICAL: This pattern allows the same name to be used in different types/columns.
   * For example, "Infrastructure" can be both a "domain" and a "use":
   * - createNodeId("domain", "Infrastructure") → "domain:Infrastructure"
   * - createNodeId("use", "Infrastructure") → "use:Infrastructure"
   *
   * @param type - The node type (e.g., 'technology', 'domain', 'use', etc.)
   * @param name - The node name
   * @returns The unique node ID in the format "type:name"
   */
  private createNodeId(type: string, name: string): string {
    return `${type}:${name}`;
  }

  /**
   * Returns the final Sankey data structure
   */
  getData(): SankeyData<Record<string, unknown>, N> {
    return this.data;
  }

  /**
   * Gets the unique ID for a node
   */
  getNodeId(name: string, type: string): string | undefined {
    const nodeId = this.createNodeId(type, name);
    return this.nodeIdMap.get(nodeId);
  }

  /**
   * Checks if a node with the given type and name exists
   */
  hasNode(name: string, type: string): boolean {
    const nodeId = this.createNodeId(type, name);
    return this.nodeIdMap.has(nodeId);
  }
}

// =============================================================================
// SANKEY NODE TYPE CONFIGURATION
// =============================================================================

/**
 * Complete configuration set for all node types in a Sankey chart
 */
export type SankeyChartConfig<T extends string> = {
  nodeTypes: Record<T, SankeyNodeTypeConfig>;
};

/**
 * Configuration for a specific node type in a Sankey chart
 */
export type SankeyNodeTypeConfig = {
  /** Display color for this node type */
  color: string;
  /** Custom label formatter for this node type */
  formatLabel?: (name: string) => string;
  /** Human-readable label for this node type */
  label: string;
};

/**
 * A node with type information for formatting
 */
export type SankeyNodeWithType<T extends string> = {
  name: string;
  type: T;
};

/**
 * Return type for chart utilities
 */
export type SankeyChartUtils<T extends string> = {
  formatNodeLabel: (node: SankeyNodeWithType<T>) => string;
  formatNodeSubtext: (node: SankeyNodeWithType<T>) => string | undefined;
  getColorMap: () => Record<string, string>;
};

/**
 * Options for creating dynamic chart utilities
 */
export type DynamicChartUtilsOptions = {
  /** All types present in the data (for pre-populating color cache) */
  allTypes?: string[];
  /** Custom formatters for specific types */
  typeFormatters?: Record<string, (name: string) => string>;
  /** Custom color mappings for specific types */
  typeColors?: Record<string, string>;
};

/**
 * Formats a type string into a human-readable label
 * Examples:
 * - "trl_level" → "TRL"
 * - "mrl_level" → "MRL"
 * - "primary_effect" → "Primary Effect"
 * - "technology" → "Technology"
 */
function formatTypeLabel(type: string): string {
  // Handle special cases
  if (type.toLowerCase().includes('trl')) {
    return 'TRL';
  }
  if (type.toLowerCase().includes('mrl')) {
    return 'MRL';
  }

  // Replace underscores with spaces and capitalize first letter of each word
  return type
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Creates utility functions for working with Sankey chart configurations (static types)
 * @param config - Static configuration with predefined node types
 */
export function createSankeyChartUtils<T extends string>(config: SankeyChartConfig<T>): SankeyChartUtils<T>;

/**
 * Creates utility functions for working with dynamic Sankey charts
 * @param colorScheme - Color scheme name to use for dynamic type coloring
 * @param options - Optional configuration for formatters and colors
 */
export function createSankeyChartUtils(
  colorScheme: SankeyColorScheme,
  options?: DynamicChartUtilsOptions,
): SankeyChartUtils<string>;

/**
 * Implementation of createSankeyChartUtils
 */
export function createSankeyChartUtils<T extends string>(
  configOrScheme: SankeyChartConfig<T> | SankeyColorScheme,
  options?: DynamicChartUtilsOptions,
): SankeyChartUtils<T> | SankeyChartUtils<string> {
  // Check if first argument is a static config
  if (typeof configOrScheme === 'object' && 'nodeTypes' in configOrScheme) {
    const config = configOrScheme;

    /**
     * Extracts color map from the configuration
     */
    const getColorMap = (): Record<T, string> => {
      const colorMap = {} as Record<T, string>;
      for (const [nodeType, typeConfig] of Object.entries(config.nodeTypes) as Array<[T, SankeyNodeTypeConfig]>) {
        colorMap[nodeType] = typeConfig.color;
      }
      return colorMap;
    };

    /**
     * Formats a node label using the configuration
     */
    const formatNodeLabel = (node: SankeyNodeWithType<T>): string => {
      const typeConfig = config.nodeTypes[node.type];
      if (typeConfig?.formatLabel) {
        return typeConfig.formatLabel(node.name);
      }
      return node.name;
    };

    /**
     * Formats node subtext (shows the node type label)
     */
    const formatNodeSubtext = (node: SankeyNodeWithType<T>): string | undefined => {
      return config.nodeTypes[node.type]?.label;
    };

    return {
      formatNodeLabel,
      formatNodeSubtext,
      getColorMap,
    };
  }

  // Dynamic color scheme mode
  const colorScheme = configOrScheme as SankeyColorScheme;
  const schemeColors = SANKEY_COLOR_SCHEMES[colorScheme];
  const typeFormatters = options?.typeFormatters || {};
  const typeColors = options?.typeColors || {};
  const allTypes = options?.allTypes || [];

  // Cache for computed colors
  const colorCache = new Map<string, string>();
  const seenTypes: string[] = [];

  // Pre-populate cache with all types if provided
  for (const type of allTypes) {
    // Check custom colors first
    if (typeColors[type]) {
      colorCache.set(type, typeColors[type]);
      seenTypes.push(type);
      continue;
    }

    // Check scheme colors
    const schemeColor = schemeColors[type.toLowerCase()];
    if (schemeColor) {
      colorCache.set(type, schemeColor);
      seenTypes.push(type);
      continue;
    }

    // Fallback to color pool
    const color = assignColorFromPool(seenTypes.length);
    colorCache.set(type, color);
    seenTypes.push(type);
  }

  /**
   * Gets color map for all types seen so far
   */
  const getColorMap = (): Record<string, string> => {
    const colorMap: Record<string, string> = {};
    for (const type of seenTypes) {
      colorMap[type] = colorCache.get(type)!;
    }
    return colorMap;
  };

  /**
   * Gets color for a type, with caching
   */
  const getColorForType = (type: string): string => {
    if (colorCache.has(type)) {
      return colorCache.get(type)!;
    }

    // Check custom colors first
    if (typeColors[type]) {
      colorCache.set(type, typeColors[type]);
      seenTypes.push(type);
      return typeColors[type];
    }

    // Check scheme colors
    const schemeColor = schemeColors[type.toLowerCase()];
    if (schemeColor) {
      colorCache.set(type, schemeColor);
      seenTypes.push(type);
      return schemeColor;
    }

    // Fallback to color pool
    const color = assignColorFromPool(seenTypes.length);
    colorCache.set(type, color);
    seenTypes.push(type);
    return color;
  };

  /**
   * Formats a node label (no custom formatting for dynamic types)
   */
  const formatNodeLabel = (node: SankeyNodeWithType<string>): string => {
    const formatter = typeFormatters[node.type];
    if (formatter) {
      return formatter(node.name);
    }
    return node.name;
  };

  /**
   * Formats node subtext (type label)
   */
  const formatNodeSubtext = (node: SankeyNodeWithType<string>): string | undefined => {
    // Ensure type is in cache/seen list for color mapping
    getColorForType(node.type);
    return formatTypeLabel(node.type);
  };

  return {
    formatNodeLabel,
    formatNodeSubtext,
    getColorMap,
  };
}

// =============================================================================
// TEXT PROCESSING UTILITIES
// =============================================================================

/**
 * Configuration object for the wrapText function
 */
export type WrapTextOptions = {
  /** Font size in pixels */
  fontSize?: number;
  /** Font weight (normal, bold, etc.) */
  fontWeight?: string;
  /** Maximum width in pixels */
  maxWidth: number;
  /** The text to wrap */
  text: string;
};

/**
 * Configuration object for the truncateText function
 */
export type TruncateTextOptions = {
  /** Font size in pixels */
  fontSize?: number;
  /** Font weight (normal, bold, etc.) */
  fontWeight?: string;
  /** Maximum width in pixels */
  maxWidth: number;
  /** The text to truncate */
  text: string;
};

/**
 * Truncates text to fit within a specified width, adding ellipsis if needed
 * @param options - Configuration object with text, maxWidth, fontSize, and fontWeight
 * @returns Truncated text that fits within the specified width
 */
// -----------------------------------------------------------------------------
// Shared text measurer (avoid creating/destroying DOM nodes per call)
// -----------------------------------------------------------------------------
let sharedMeasureSvg: d3.Selection<SVGSVGElement, unknown, HTMLElement, unknown> | undefined;
let sharedMeasureText: d3.Selection<SVGTextElement, unknown, HTMLElement, unknown> | undefined;

function ensureTextMeasurer(): d3.Selection<SVGTextElement, unknown, HTMLElement, unknown> {
  if (!sharedMeasureSvg || !sharedMeasureText) {
    const svg = select('body')
      .append('svg')
      .style('visibility', 'hidden')
      .style('position', 'absolute')
      .style('top', '-10000px')
      .style('left', '-10000px');
    const text = svg.append('text');
    sharedMeasureSvg = svg;
    sharedMeasureText = text;
  }
  return sharedMeasureText!;
}

export function truncateText(options: TruncateTextOptions): string {
  const { text, maxWidth, fontSize = 12, fontWeight = '700' } = options;

  if (maxWidth <= 0) return text; // No truncation if maxWidth is 0 or negative

  const textElement = ensureTextMeasurer().style('font-size', `${fontSize}px`).style('font-weight', fontWeight);

  // Check if the full text fits
  textElement.text(text);
  const fullTextLength = (textElement.node() as SVGTextElement)?.getComputedTextLength() || 0;

  if (fullTextLength <= maxWidth) {
    return text;
  }

  // Binary search to find the maximum length that fits with ellipsis
  let low = 0;
  let high = text.length;
  let bestFit = '';

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const truncated = text.substring(0, mid) + '...';

    textElement.text(truncated);
    const truncatedLength = (textElement.node() as SVGTextElement)?.getComputedTextLength() || 0;

    if (truncatedLength <= maxWidth) {
      bestFit = truncated;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return bestFit || '...';
}

/**
 * Wraps text to fit within a specified width, returning an array of text lines
 * @param options - Configuration object with text, maxWidth, fontSize, and fontWeight
 * @returns Array of text lines that fit within the specified width
 */
export function wrapText(options: WrapTextOptions): string[] {
  const { text, maxWidth, fontSize = 12, fontWeight = '700' } = options;

  const textElement = ensureTextMeasurer().style('font-size', `${fontSize}px`).style('font-weight', fontWeight);

  const words = text.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    textElement.text(testLine);
    const textLength = (textElement.node() as SVGTextElement)?.getComputedTextLength() || 0;

    if (textLength > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Calculates the actual width of text lines
 * @param lines - Array of text lines
 * @param fontSize - Font size in pixels
 * @param fontWeight - Font weight (normal, bold, etc.)
 * @returns Maximum width of all lines
 */
export function calculateTextWidth(lines: string[], fontSize = 12, fontWeight = '700'): number {
  if (!lines.length) return 0;

  const textElement = ensureTextMeasurer().style('font-size', `${fontSize}px`).style('font-weight', fontWeight);

  let maxWidth = 0;
  for (const line of lines) {
    textElement.text(line);
    const textLength = (textElement.node() as SVGTextElement)?.getComputedTextLength() || 0;
    maxWidth = Math.max(maxWidth, textLength);
  }

  return maxWidth;
}

// =============================================================================
// LAYOUT CALCULATION UTILITIES
// =============================================================================

/**
 * Calculates the optimal height for a Sankey diagram based on node count and distribution
 * @param data - The Sankey data containing nodes and links
 * @param nodePadding - Padding between nodes
 * @param isAiGenerated - Whether this is an AI-generated Sankey (uses more generous calculation)
 * @returns Calculated height in pixels
 */
export function calculateAutoHeight<L = Record<string, unknown>, N = Record<string, unknown>>(
  data: SankeyData<L, N>,
  nodePadding: number,
  isAiGenerated = false,
): number {
  const DEFAULT_HEIGHT = 500;
  const baseHeight = DEFAULT_HEIGHT;

  // AI-generated Sankeys need more generous height due to unpredictable flow distributions
  // Platform Sankeys use the proven formula that has worked reliably
  const minNodeHeight = isAiGenerated ? 24 : 16;

  if (!data.nodes.length) return baseHeight;

  // Perform topological sort to determine actual column assignments
  // This mimics what the Sankey component does to assign depths
  const categoryOrder = new Map<string, number>();
  const categorySet = new Set<string>();

  // Collect all categories
  data.nodes.forEach((node) => {
    const category = node.category || 'default';
    categorySet.add(category);
  });

  // Build directed graph to determine category ordering
  const categoryGraph = new Map<string, Set<string>>();
  const inDegree = new Map<string, number>();

  categorySet.forEach((cat) => {
    categoryGraph.set(cat, new Set());
    inDegree.set(cat, 0);
  });

  // Build a map for fast node lookup by ID (using type:name pattern)
  const nodeIdMap = new Map<string, SankeyNodeData<N>>();
  data.nodes.forEach((node) => {
    const nodeId = node.id || node.name;
    nodeIdMap.set(nodeId, node);
  });

  // Analyze links to build category flow
  data.links.forEach((link) => {
    const sourceNode = nodeIdMap.get(String(link.source));
    const targetNode = nodeIdMap.get(String(link.target));

    if (sourceNode && targetNode) {
      const sourceCat = sourceNode.category || 'default';
      const targetCat = targetNode.category || 'default';

      if (sourceCat !== targetCat && !categoryGraph.get(sourceCat)?.has(targetCat)) {
        categoryGraph.get(sourceCat)?.add(targetCat);
        inDegree.set(targetCat, (inDegree.get(targetCat) || 0) + 1);
      }
    }
  });

  // Topological sort to determine column order
  const sortedCategories: string[] = [];
  const queue: string[] = [];

  inDegree.forEach((degree, cat) => {
    if (degree === 0) {
      queue.push(cat);
    }
  });

  while (queue.length > 0) {
    const current = queue.shift()!;
    sortedCategories.push(current);

    categoryGraph.get(current)?.forEach((neighbor) => {
      const newDegree = (inDegree.get(neighbor) || 0) - 1;
      inDegree.set(neighbor, newDegree);
      if (newDegree === 0) {
        queue.push(neighbor);
      }
    });
  }

  // Handle remaining categories (cycles or disconnected)
  categorySet.forEach((cat) => {
    if (!sortedCategories.includes(cat)) {
      sortedCategories.push(cat);
    }
  });

  // Assign column indices to categories
  sortedCategories.forEach((cat, index) => {
    categoryOrder.set(cat, index);
  });

  // Group nodes by their assigned column
  const nodesByColumn = new Map<number, SankeyNodeData<N>[]>();
  data.nodes.forEach((node) => {
    const category = node.category || 'default';
    const column = categoryOrder.get(category) ?? 0;
    if (!nodesByColumn.has(column)) {
      nodesByColumn.set(column, []);
    }
    nodesByColumn.get(column)!.push(node);
  });

  // Find the maximum number of nodes in any column
  const maxNodesInColumn = Math.max(...Array.from(nodesByColumn.values()).map((nodes) => nodes.length));

  // Calculate minimum required height based on the densest column
  // NOTE: This uses the original formula that worked well for platform Sankeys.
  // For AI-generated Sankeys with unpredictable distributions, the Sankey component
  // will dynamically adjust the height upward if needed after D3 layout.
  const minRequiredHeight = maxNodesInColumn * minNodeHeight + (maxNodesInColumn - 1) * nodePadding;

  // Also calculate based on total nodes to ensure enough vertical space
  const heightBasedOnTotal = data.nodes.length * minNodeHeight;

  // Return the larger of all calculated heights
  return Math.max(baseHeight, minRequiredHeight, heightBasedOnTotal);
}

/**
 * Font size presets for different Sankey chart sizes
 */
export const SANKEY_FONT_SIZE_MAP: Record<
  'xs' | 'sm' | 'md',
  { labelFontSize: number; subtextFontSize: number; labelSubtextGap: number }
> = {
  xs: {
    labelFontSize: 11,
    subtextFontSize: 10,
    labelSubtextGap: 12,
  },
  sm: { labelFontSize: 12, subtextFontSize: 11, labelSubtextGap: 13 },
  md: { labelFontSize: 13, subtextFontSize: 12, labelSubtextGap: 15 },
};

/**
 * Automatically determine an appropriate size preset based on the number of nodes.
 * Larger diagrams use smaller font presets.
 */
export function calculateSankeySize<L, N>(data: SankeyData<L, N>): 'xs' | 'sm' | 'md' {
  const nodeCount = data.nodes.length;
  if (nodeCount > 20) return 'xs';
  if (nodeCount > 10) return 'sm';
  // For any smaller diagram, use the medium preset
  return 'md';
}

/**
 * Automatically determine the optimal size preset based on both the data complexity
 * and the available width space. This ensures labels fit comfortably without overlapping.
 */
export function calculateOptimalSankeySize<L, N>(
  data: SankeyData<L, N>,
  columnWidth: number,
  formatNodeLabel?: (node: SankeyNodeData<N>) => string,
  formatNodeSubtext?: (node: SankeyNodeData<N>) => string | undefined,
): 'xs' | 'sm' | 'md' {
  const nodeCount = data.nodes.length;

  // Start with the size based on node count (existing logic)
  let baseSize: 'xs' | 'sm' | 'md';
  if (nodeCount > 20) baseSize = 'xs';
  else if (nodeCount > 10) baseSize = 'sm';
  else baseSize = 'md';

  const sizes: ('xs' | 'sm' | 'md')[] = ['xs', 'sm', 'md'];
  const baseIndex = sizes.indexOf(baseSize);

  // Try to upgrade to larger sizes if we have enough space
  for (let i = baseIndex + 1; i < sizes.length; i++) {
    const testSize = sizes[i];
    const testFontSize = SANKEY_FONT_SIZE_MAP[testSize].labelFontSize;

    if (canSizeWork(data, columnWidth, testFontSize, formatNodeLabel, formatNodeSubtext)) {
      baseSize = testSize;
    } else {
      break; // If this size doesn't work, larger ones won't either
    }
  }

  return baseSize;
}

/**
 * Helper function to estimate if a given font size will work without causing overlaps
 */
export function canSizeWork<L, N>(
  data: SankeyData<L, N>,
  columnWidth: number,
  testLabelFontSize: number,
  formatNodeLabel?: (node: SankeyNodeData<N>) => string,
  formatNodeSubtext?: (node: SankeyNodeData<N>) => string | undefined,
): boolean {
  const DEFAULT_NODE_WIDTH = 12;
  const RECT_PADDING_X = 2;
  const LABEL_MARGIN_RATIO = 0.15;
  const MAX_LABEL_MARGIN = 64;
  const INNER_COLUMN_AVAILABLE_RATIO = 0.65;
  const MIN_LABEL_WIDTH_RATIO = 0.15;
  const MIN_LABEL_WIDTH_ABS = 20;

  const nodeWidth = DEFAULT_NODE_WIDTH;
  const rectPaddingX = RECT_PADDING_X;
  const textMargin = Math.min(MAX_LABEL_MARGIN, columnWidth * LABEL_MARGIN_RATIO);

  const gapWidth = columnWidth - nodeWidth - textMargin - rectPaddingX * 2;

  const minDynamicWidth = Math.max(MIN_LABEL_WIDTH_ABS, columnWidth * MIN_LABEL_WIDTH_RATIO);

  const availableWidth = Math.max(minDynamicWidth, gapWidth * INNER_COLUMN_AVAILABLE_RATIO);

  // Estimate the average label width with the test font size
  let maxEstimatedWidth = 0;

  for (const node of data.nodes) {
    const labelText = formatNodeLabel?.(node) ?? node.name;
    const subtextText = formatNodeSubtext?.(node) ?? undefined;

    // Rough estimation: assume average character width is 0.6 * fontSize
    const avgCharWidth = testLabelFontSize * 0.6;
    const estimatedLabelWidth = labelText.length * avgCharWidth;

    let estimatedSubtextWidth = 0;
    if (subtextText) {
      // Subtext is usually slightly smaller
      const subtextFontSize = testLabelFontSize * 0.9;
      estimatedSubtextWidth = subtextText.length * (subtextFontSize * 0.6);
    }

    const estimatedTotalWidth = Math.max(estimatedLabelWidth, estimatedSubtextWidth);
    maxEstimatedWidth = Math.max(maxEstimatedWidth, estimatedTotalWidth);
  }

  // Allow some safety margin (80% of available width)
  return maxEstimatedWidth <= availableWidth * 0.8;
}

// =============================================================================
// HELPER UTILITIES
// =============================================================================

/**
 * Generates a unique ID for SVG elements
 * @param prefix - Prefix for the ID
 * @returns A unique ID string
 */
export function generateUID(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
}

// =============================================================================
// PROCESSED NODE/LINK TYPES (for utility functions)
// =============================================================================

/**
 * Internal type for processed nodes after d3-sankey layout
 */
export type ProcessedNode<T = Record<string, unknown>> = SankeyNodeData<T> & {
  depth?: number;
  height?: number;
  index?: number;
  layer?: number;
  sourceLinks?: ProcessedLink<unknown>[];
  targetLinks?: ProcessedLink<unknown>[];
  value?: number;
  x0?: number;
  x1?: number;
  y0?: number;
  y1?: number;
};

/**
 * Internal type for processed links after d3-sankey layout
 */
export type ProcessedLink<T = Record<string, unknown>> = {
  index?: number;
  source: ProcessedNode<unknown>;
  target: ProcessedNode<unknown>;
  value: number;
  width?: number;
  y0?: number;
  y1?: number;
} & T;

/**
 * Helper function to safely extract SankeyNodeData properties from a ProcessedNode
 * This allows us to use formatNodeLabel with ProcessedNode without unsafe casting
 */
export function extractNodeData<N>(processedNode: ProcessedNode<unknown>): SankeyNodeData<N> {
  // Extract only the SankeyNodeData properties, excluding the ProcessedNode-specific ones
  const { depth, height, index, layer, sourceLinks, targetLinks, value, x0, x1, y0, y1, ...nodeData } = processedNode;
  return nodeData as SankeyNodeData<N>;
}

// =============================================================================
// CONNECTED ELEMENTS UTILITIES
// =============================================================================

/**
 * Helper function to find all links that are part of the flow paths connected to a given node.
 * It works for any processed node irrespective of the custom node or link payload generics, so we
 * purposefully keep the generic parameters as `unknown` to avoid the need for explicit casting at
 * the call site.
 *
 * This is used for highlighting the connected links when hovering over a node.
 */
export function findConnectedLinks(node: ProcessedNode<unknown>): ProcessedLink<unknown>[] {
  const connectedLinks = new Set<ProcessedLink<unknown>>();

  // Traverse forward (downstream) from the node
  const traverseForward = (currentNode: ProcessedNode<unknown>, visited: Set<ProcessedNode<unknown>>) => {
    if (visited.has(currentNode)) return;
    visited.add(currentNode);

    currentNode.sourceLinks?.forEach((link) => {
      connectedLinks.add(link);
      traverseForward(link.target, visited);
    });
  };

  // Traverse backward (upstream) from the node
  const traverseBackward = (currentNode: ProcessedNode<unknown>, visited: Set<ProcessedNode<unknown>>) => {
    if (visited.has(currentNode)) return;
    visited.add(currentNode);

    currentNode.targetLinks?.forEach((link) => {
      connectedLinks.add(link);
      traverseBackward(link.source, visited);
    });
  };

  // Start traversal from the hovered node
  traverseForward(node, new Set());
  traverseBackward(node, new Set());

  return Array.from(connectedLinks);
}

/**
 * Helper function to find all nodes that are part of the flow paths connected to a given node.
 * This includes the node itself and all nodes that are upstream or downstream from it.
 *
 * This is used for highlighting the connected node labels when hovering over a node.
 */
export function findConnectedNodes(node: ProcessedNode<unknown>): ProcessedNode<unknown>[] {
  const connectedNodes = new Set<ProcessedNode<unknown>>();

  // Traverse forward (downstream) from the node
  const traverseForward = (currentNode: ProcessedNode<unknown>, visited: Set<ProcessedNode<unknown>>) => {
    if (visited.has(currentNode)) return;
    visited.add(currentNode);
    connectedNodes.add(currentNode);

    currentNode.sourceLinks?.forEach((link) => {
      traverseForward(link.target, visited);
    });
  };

  // Traverse backward (upstream) from the node
  const traverseBackward = (currentNode: ProcessedNode<unknown>, visited: Set<ProcessedNode<unknown>>) => {
    if (visited.has(currentNode)) return;
    visited.add(currentNode);
    connectedNodes.add(currentNode);

    currentNode.targetLinks?.forEach((link) => {
      traverseBackward(link.source, visited);
    });
  };

  // Start traversal from the hovered node
  traverseForward(node, new Set());
  traverseBackward(node, new Set());

  return Array.from(connectedNodes);
}

/**
 * Highlights connected links and node labels by dimming all elements and then brightening the specified ones
 */
export function highlightConnectedElements({
  connectedLinks,
  connectedNodes,
  linkOpacity,
  svg,
  dimOthers = true,
  dimOpacity,
  highlightOpacity,
}: {
  connectedLinks: ProcessedLink<unknown>[];
  connectedNodes: ProcessedNode<unknown>[];
  linkOpacity: number;
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  dimOthers?: boolean;
  dimOpacity?: number;
  highlightOpacity?: number;
}) {
  // Cancel any in-flight transitions to avoid flashing
  const allLinks = svg.selectAll<SVGPathElement, unknown>('.sankey-links path').interrupt();
  const allRects = svg.selectAll<SVGRectElement, unknown>('.sankey-node > rect').interrupt();
  const allLabels = svg.selectAll<SVGGElement, unknown>('.sankey-node-labels').interrupt();

  if (dimOthers) {
    const dim = dimOpacity ?? linkOpacity * 0.35;
    allLinks.attr('stroke-opacity', dim);
    allRects.style('opacity', 0.3);
    allLabels.style('opacity', 0.3);
  }

  // Highlight only the connected ones (no transitions)
  // Keep highlighted links at their base opacity unless caller overrides
  const hl = highlightOpacity ?? linkOpacity;
  connectedLinks.forEach((link) => {
    svg
      .selectAll('.sankey-links path')
      .filter((pathData) => pathData === link)
      .attr('stroke-opacity', hl);
  });

  connectedNodes.forEach((node) => {
    const nodeGroup = svg.selectAll('.sankey-node').filter((nodeData) => nodeData === node);
    nodeGroup.select('.sankey-node-labels').style('opacity', 1);
    nodeGroup.select('rect').style('opacity', 1);
  });
}

/**
 * Resets all links and node labels to their default opacity
 */
export function resetElementsOpacity(svg: d3.Selection<SVGSVGElement, unknown, null, undefined>, linkOpacity: number) {
  // Instant reset, no animations
  svg.selectAll<SVGPathElement, unknown>('.sankey-links path').interrupt().attr('stroke-opacity', linkOpacity);
  svg.selectAll('.sankey-node rect').interrupt().style('opacity', 1);
  svg.selectAll('.sankey-node-labels').interrupt().style('opacity', 1);
}
